import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OnboardingProvider } from './components/onboarding';
import { OfflineProvider } from './contexts/OfflineContext';
import { OfflineIndicator } from './components/OfflineIndicator';
import UpdateBanner from './components/UpdateBanner';
import { Toaster } from './components/ui/sonner';
import { LoadingState } from './components/ui/LoadingState';
import LandingPage from './pages/LandingPage';
import CinematicLandingExperiment from './pages/experimental/CinematicLandingExperiment';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import TreeViewEnhanced from './pages/TreeViewEnhanced';
import PersonDetail from './pages/PersonDetail';
import LegacyBookPreviewPage from './pages/LegacyBookPreviewPage';
import TreeLegacyBookPreviewPage from './pages/TreeLegacyBookPreviewPage';
import TreeLegacyBookPrintExportPage from './pages/TreeLegacyBookPrintExportPage';
import Timeline from './pages/Timeline';
import Settings from './pages/Settings';
import TreeMembers from './pages/TreeMembers';
import AcceptInvite from './pages/AcceptInvite';
import AcceptFamilySpaceInvite from './pages/AcceptFamilySpaceInvite';
import GeneEngine from './pages/GeneEngine';
import DNAManagement from './pages/DNAManagement';
import ConnectionRequests from './pages/ConnectionRequests';
import FAQ from './pages/FAQ';
import FamilySpaces from './pages/FamilySpaces';
import Pricing from './pages/Pricing';
import BillingCheckout from './pages/BillingCheckout';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';
import PublishedMemoryView from './pages/PublishedMemoryView';
import PublishedTributeView from './pages/PublishedTributeView';
import MemorialPublicPage from './pages/MemorialPublicPage';
import MemorialsPage from './pages/MemorialsPage';
import MemorialManagePage from './pages/MemorialManagePage';
import MemorialQrRedirect from './pages/MemorialQrRedirect';
import TributeTokenRedirect from './pages/TributeTokenRedirect';
import RemindersPage from './pages/RemindersPage';
import StorybookPage from './pages/StorybookPage';
import StoryStudioPage from './pages/StoryStudioPage';
import HandoverVaultPage from './pages/HandoverVaultPage';
import FeatureGuidePage from './pages/FeatureGuidePage';
import HelpGuidesPage from './pages/HelpGuidesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PartnersPage from './pages/PartnersPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import AdminInsightsPage from './pages/AdminInsightsPage';
import AdminProductRequestsPage from './pages/AdminProductRequestsPage';
import { BridgeAssistantRoot } from './components/assistant';
import { isFounderAdmin } from './utils/adminAccess';
import '@/App.css';

const SHOWCASE_MODE = process.env.REACT_APP_SHOWCASE_MODE === 'true';

function BridgeAssistantGate() {
  const { user, loading } = useAuth();
  if (loading && !SHOWCASE_MODE) return null;
  if (!user && !SHOWCASE_MODE) return null;
  return <BridgeAssistantRoot />;
}

const ProtectedRoute = ({ children }) => {
  const { user, token, loading } = useAuth();

  if (loading && !SHOWCASE_MODE) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Loading..." variant="spinner" />
      </div>
    );
  }

  // In showcase mode, bypass login — always show the app
  if (SHOWCASE_MODE) return children;
  return user && token ? children : <Navigate to="/login" />;
};

const FounderAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading && !SHOWCASE_MODE) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Loading..." variant="spinner" />
      </div>
    );
  }

  if (SHOWCASE_MODE) return children;
  if (!user) return <Navigate to="/login" />;
  if (!isFounderAdmin(user)) return <Navigate to="/dashboard" replace />;
  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  const authBootstrapping = loading && !SHOWCASE_MODE;

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/experimental/cinematic" element={<CinematicLandingExperiment />} />
      <Route
        path="/login"
        element={
          authBootstrapping ? (
            <div className="min-h-screen flex items-center justify-center">
              <LoadingState message="Loading..." variant="spinner" />
            </div>
          ) : user ? (
            <Navigate to="/dashboard" />
          ) : (
            <Login />
          )
        }
      />
      <Route
        path="/register"
        element={
          authBootstrapping ? (
            <div className="min-h-screen flex items-center justify-center">
              <LoadingState message="Loading..." variant="spinner" />
            </div>
          ) : user ? (
            <Navigate to="/dashboard" />
          ) : (
            <Register />
          )
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/accept-family-space-invite/:token" element={<AcceptFamilySpaceInvite />} />
      <Route path="/memory/:slug" element={<PublishedMemoryView />} />
      <Route path="/remember/:slug" element={<PublishedTributeView />} />
      <Route path="/memorial/:slug" element={<MemorialPublicPage />} />
      <Route path="/q/:shortCode" element={<MemorialQrRedirect />} />
      <Route path="/q/memorial/:qrId" element={<MemorialQrRedirect />} />
      <Route path="/t/:token" element={<TributeTokenRedirect />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/family-spaces" element={<ProtectedRoute><FamilySpaces /></ProtectedRoute>} />
      <Route path="/gene-engine" element={<ProtectedRoute><GeneEngine /></ProtectedRoute>} />
      <Route path="/tree/:treeId/legacy-book-print" element={<TreeLegacyBookPrintExportPage />} />
      <Route path="/tree/:treeId/legacy-book-preview" element={<ProtectedRoute><TreeLegacyBookPreviewPage /></ProtectedRoute>} />
      <Route path="/tree/:treeId" element={<ProtectedRoute><TreeViewEnhanced /></ProtectedRoute>} />
      <Route path="/family/:familySlug" element={<ProtectedRoute><TreeViewEnhanced /></ProtectedRoute>} />
      <Route path="/tree/:treeId/members" element={<ProtectedRoute><TreeMembers /></ProtectedRoute>} />
      <Route path="/family/:familySlug/members" element={<ProtectedRoute><TreeMembers /></ProtectedRoute>} />
      <Route path="/person/:personId" element={<ProtectedRoute><PersonDetail /></ProtectedRoute>} />
      <Route path="/person/:personId/legacy-book" element={<ProtectedRoute><LegacyBookPreviewPage /></ProtectedRoute>} />
      <Route path="/timeline/:treeId" element={<ProtectedRoute><Timeline /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/dna" element={<ProtectedRoute><DNAManagement /></ProtectedRoute>} />
      <Route path="/connection-requests" element={<ProtectedRoute><ConnectionRequests /></ProtectedRoute>} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/help" element={<HelpGuidesPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/support" element={<Navigate to="/faq" replace />} />
      <Route path="/billing/checkout" element={<ProtectedRoute><BillingCheckout /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
      <Route path="/memorials" element={<ProtectedRoute><MemorialsPage /></ProtectedRoute>} />
      <Route path="/memorials/manage/:personRef" element={<ProtectedRoute><MemorialManagePage /></ProtectedRoute>} />
      <Route path="/storybook" element={<ProtectedRoute><StorybookPage /></ProtectedRoute>} />
      <Route path="/storybook/:storybookId" element={<ProtectedRoute><StorybookPage /></ProtectedRoute>} />
      <Route path="/story-studio" element={<ProtectedRoute><StoryStudioPage /></ProtectedRoute>} />
      <Route path="/vault" element={<ProtectedRoute><HandoverVaultPage /></ProtectedRoute>} />
      <Route path="/guide" element={<ProtectedRoute><FeatureGuidePage /></ProtectedRoute>} />
      <Route path="/admin/insights" element={<FounderAdminRoute><AdminInsightsPage /></FounderAdminRoute>} />
      <Route path="/admin/product-requests" element={<FounderAdminRoute><AdminProductRequestsPage /></FounderAdminRoute>} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <div className="App noise-overlay">
        <BrowserRouter>
          <OfflineProvider>
            <AuthProvider>
              <OnboardingProvider>
                <OfflineIndicator />
                <UpdateBanner />
                <AppRoutes />
                <BridgeAssistantGate />
                <Toaster />
              </OnboardingProvider>
            </AuthProvider>
          </OfflineProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;